import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Academy,
  AcademyOperatingProfileInput,
  AcademySport,
  UpdateAcademySportsPayload,
} from '@court-sync/shared';
import { academyService } from '../services/academy';
import { STALE_TIME, queryClient } from '../queryClient';
import { queryKeys } from './keys';
import { unwrapApiData } from './unwrap';
import { updateAcademyQuery } from './academy';

export interface AcademySportsBundle {
  sports: AcademySport[];
  operatingProfile?: AcademyOperatingProfileInput | null;
}

const EMPTY_ACADEMY_SPORTS: AcademySport[] = [];

/** Supports legacy array responses and the bundled `{ sports, operatingProfile }` shape. */
export function normalizeAcademySportsBundle(
  data: AcademySport[] | AcademySportsBundle | null | undefined
): AcademySportsBundle {
  if (!data) {
    return { sports: EMPTY_ACADEMY_SPORTS, operatingProfile: null };
  }
  if (Array.isArray(data)) {
    return { sports: data, operatingProfile: null };
  }
  return {
    sports: data.sports ?? EMPTY_ACADEMY_SPORTS,
    operatingProfile: data.operatingProfile ?? null,
  };
}

export async function fetchAcademySportsQuery(
  academyId: string,
  options?: { force?: boolean }
): Promise<AcademySportsBundle> {
  const key = queryKeys.academySports.list(academyId);
  if (options?.force) {
    await queryClient.invalidateQueries({ queryKey: key });
  }
  return queryClient.fetchQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await academyService.getAcademySports(academyId);
      return normalizeAcademySportsBundle(
        unwrapApiData(response, 'Failed to load academy sports')
      );
    },
    staleTime: STALE_TIME.MEDIUM,
  });
}

export function useAcademySports(academyId?: string | null, options?: { enabled?: boolean }) {
  const queryKey = queryKeys.academySports.list(academyId ?? '');
  const enabled = !!academyId && (options?.enabled ?? true);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await academyService.getAcademySports(academyId!);
      return normalizeAcademySportsBundle(
        unwrapApiData(response, 'Failed to load academy sports')
      );
    },
    enabled,
    staleTime: STALE_TIME.MEDIUM,
  });

  const refresh = useCallback(
    async (opts?: { force?: boolean }) => {
      if (!academyId) return undefined;
      if (opts?.force) {
        await queryClient.invalidateQueries({ queryKey });
      }
      return refetch();
    },
    [academyId, queryKey, refetch]
  );

  return {
    sports: data?.sports ?? EMPTY_ACADEMY_SPORTS,
    operatingProfile: data?.operatingProfile ?? null,
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refresh,
  };
}

export function useUpdateAcademySports(academyId: string) {
  const queryClient = useQueryClient();
  const sportsKey = queryKeys.academySports.list(academyId);
  const academyKey = queryKeys.academies.detail(academyId);

  return useMutation({
    mutationFn: async (payload: UpdateAcademySportsPayload) => {
      const response = await academyService.updateAcademySports(academyId, payload);
      if (!response.success) {
        throw new Error(response.error ?? 'Failed to update sports');
      }
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      return data?.sports ?? [];
    },
    onSuccess: async (sports) => {
      queryClient.setQueryData(sportsKey, (old: AcademySportsBundle | undefined) => ({
        sports,
        operatingProfile: old?.operatingProfile ?? null,
      }));
      patchAcademySportsOnAcademyCache(academyId, sports);
      await queryClient.invalidateQueries({ queryKey: academyKey });
    },
  });
}

export function useUpdateOperatingProfile(academyId: string) {
  const queryClient = useQueryClient();
  const sportsKey = queryKeys.academySports.list(academyId);

  return useMutation({
    mutationFn: (profile: Partial<AcademyOperatingProfileInput>) =>
      academyService.updateOperatingProfile(academyId, profile),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sportsKey });
      await queryClient.invalidateQueries({ queryKey: queryKeys.academies.detail(academyId) });
    },
  });
}

export function invalidateAcademySportsQueries(academyId?: string): void {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.academySports.all,
    ...(academyId ? { predicate: (q) => q.queryKey.includes(academyId) } : {}),
  });
}

/** Merge sports onto academy detail cache after sports update. */
export function patchAcademySportsOnAcademyCache(academyId: string, sports: AcademySport[]): void {
  const detailKey = queryKeys.academies.detail(academyId);
  const existing = queryClient.getQueryData<Academy>(detailKey);
  if (existing) {
    queryClient.setQueryData<Academy>(detailKey, { ...existing, academySports: sports });
  }
  const userKey = queryKeys.academies.user();
  queryClient.setQueryData<Academy[]>(userKey, (old) =>
    old?.map((a) => (a.id === academyId ? { ...a, academySports: sports } : a))
  );
}

export async function updateOperatingProfileQuery(
  academyId: string,
  profile: Partial<AcademyOperatingProfileInput>
): Promise<void> {
  await academyService.updateOperatingProfile(academyId, profile);
  await fetchAcademySportsQuery(academyId, { force: true });
  await updateAcademyQuery(academyId, { operatingProfile: profile as Academy['operatingProfile'] });
}
